/* eslint-disable no-unused-vars */

import moment from 'moment';
import _ from 'lodash';

import { removeUnavailableTreeData } from '@components/tree-component';
import { getStructureData } from '@utilities/structure-search';

import { getDataPoints } from '@pages/settings/helper';
import { cleanString } from '@utilities/attachments';
import {
  createAtOrSyncedDateFilter,
  dueDateFilter,
  scheduledDateFilter
} from '@utilities/date-filters';
import { sortFilterTags } from '@utilities/filter-is-changed';

export const getFilterStructure = ({
  filters,
  sitesMap,
  structures,
  SelectedFilter,
  jurisdictionNumbersMap,
  picklistsMap,
  structureDisplayFields,
  cacheSites,
  siteIdFor,
  store
}) => {
  const {
    area,
    proximity,
    siteType,
    siteStatus,
    searched = '',
    structureStatus,
    structureType,
    taskType,
    taskStatus,
    priority,
    workOrderPriority,
    assign,
    scheduledDate,
    download,
    dueDate: filterDueDate,
    recurringTemplate,
    workOrderType = [],
    createdAt,
    syncedDate: syncedDateFilter,
    checkRecurrancePriority,
    inspectionType,
    checkType
  } = filters;

  const sitesSiteTypeData = {},
    sitesSiteStatusData = {},
    structuresStructureStatusData = {},
    structuresStructureTypeData = {},
    sitesAreaHierarchyTreeData = {},
    sitesWorkOrderPriorityDataMap = {};

  const selectedWorkOrderRecurringTemplateMap =
    recurringTemplate &&
    recurringTemplate.value &&
    recurringTemplate.value.reduce((acc, curr) => {
      const rtName = (curr && curr.split('-') && curr.split('-').at(-1)).trim();
      acc[rtName] = true;
      return acc;
    }, {});

  const inspectionTypeMap = inspectionType?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const selectedRecurrancePriority = checkRecurrancePriority?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const selectedAreaMap = area?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});
  const workOrderTypeMap = workOrderType?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});
  const selectedSiteTypeMap = siteType?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});
  const selectedSiteStatusMap = siteStatus?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});
  const selectedStructureTypeMap = structureType?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});
  const selectedStructureStatusMap = structureStatus?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const checkTypeMap = checkType?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const selectedTaskType = taskType?.value?.includes('All')
    ? {
        'Work Order': true,
        'Work Request': true,
        Inspection: true,
        Check: true,
        'New or edited asset': true
      }
    : taskType?.value?.reduce((acc, curr) => {
        acc[
          curr === 'Work orders'
            ? 'Work Order'
            : curr === 'Work requests'
            ? 'Work Request'
            : curr === 'Inspections'
            ? 'Inspection'
            : curr === 'Check'
            ? 'Check'
            : curr === 'New or edited asset'
            ? 'New or edited asset'
            : 'Work Order'
        ] = true;
        return acc;
      }, {});

  const selectedTaskStatus = taskStatus?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const selectedWorkOrderPriority = workOrderPriority?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});
  const selectedPriority = priority?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const structureWithDataPoints = structures?.map((props) => {
    const { jurisdictionNumber } = jurisdictionNumbersMap[props.structureId] || {};

    const dataPoints = getDataPoints(
      { ...props, subtype: props.structureType, jurisdictionNumber },
      structureDisplayFields,
      'structure',
      picklistsMap
    );
    return {
      ...props,
      dataPoints
    };
  });
  let filteredStructure = structureWithDataPoints
    .filter((structure) => {
      const { siteId, status, structureType: type, allTasksForStructure } = structure;

      if (siteIdFor && siteId !== siteIdFor) return false;

      const site = cacheSites[sitesMap[siteId]] || {};

      sitesSiteTypeData[site?.siteType] = true;
      sitesSiteStatusData[site?.status] = true;
      sitesAreaHierarchyTreeData[site?.areaHierarchy] = true;

      structuresStructureTypeData[type] = true;
      structuresStructureStatusData[status] = true;

      if (!getStructureData({ inputValue: searched, structure, store })) return false;

      if (area.value.length > 0 && !selectedAreaMap[site?.areaHierarchy]) return false;
      if (siteType.value.length > 0 && !selectedSiteTypeMap[site?.siteType]) return false;
      if (siteStatus.value.length > 0 && !selectedSiteStatusMap[site?.status]) return false;
      if (structureType.value.length > 0 && !selectedStructureTypeMap[type]) return false;
      if (structureStatus.value.length > 0 && !selectedStructureStatusMap[status]) return false;
      if (proximity.value) {
        if (!site?.distance) return false;
        if (site.distance >= proximity.value) return false;
      }
      const dueCon = _.isArray(filterDueDate.value) ? !filterDueDate.value.includes('') : true;
      const scheduleCon = _.isArray(scheduledDate.value) ? !scheduledDate.value.includes('') : true;

      const createAtCon = _.isArray(createdAt?.value) ? !createdAt?.value.includes('') : true;
      const syncedDateCon = _.isArray(syncedDateFilter?.value)
        ? !syncedDateFilter?.value.includes('')
        : true;

      if (
        recurringTemplate.value.length > 0 &&
        !allTasksForStructure.some((x) => selectedWorkOrderRecurringTemplateMap[x.rtName])
      )
        return false;

      if (
        checkRecurrancePriority?.value?.length > 0 &&
        !allTasksForStructure.some(
          (x) => x.type === 'Check' && selectedRecurrancePriority[x.recurrencePriorityId]
        )
      )
        return false;

      if (
        inspectionType?.value?.length > 0 &&
        !allTasksForStructure.some((x) => {
          return x.type === 'Check' && inspectionTypeMap[x.inspectionFormId?.toString()];
        })
      )
        return false;

      if (
        checkType?.value.length > 0 &&
        !allTasksForStructure.some((x) => checkTypeMap[x.inspectionTypeId])
      )
        return false;

      if (
        workOrderType?.value?.length > 0 &&
        !allTasksForStructure.some((x) => workOrderTypeMap[x?.typeId])
      )
        return false;

      if (
        taskType?.value?.length > 0 &&
        !(
          allTasksForStructure?.some(
            (x) => selectedTaskType[x?.newWorkRequest ? 'Work Request' : x.type]
          ) || structure?.allEditEquipmentAssets?.some((x) => selectedTaskType[x.type])
        )
      )
        return false;

      if (
        taskStatus?.value?.length > 0 &&
        !allTasksForStructure?.some((x) => selectedTaskStatus[x.status])
      )
        return false;

      allTasksForStructure?.map((x) => {
        if (!sitesWorkOrderPriorityDataMap[x.priority])
          sitesWorkOrderPriorityDataMap[x.priority] = true;
      });

      if (
        allTasksForStructure?.some((x) => x.type === 'Checks') &&
        priority.value.length > 0 &&
        !allTasksForStructure.some((x) => selectedPriority[x.priority])
      )
        return false;

      if (
        workOrderPriority.value.length > 0 &&
        !allTasksForStructure?.some(
          (x) => x.type === 'Work Order' && selectedWorkOrderPriority[x.priority]
        )
      )
        return false;

      if (
        assign?.value?.length > 0 &&
        !allTasksForStructure.some((y) =>
          assign.value.some((r) => y.customAssignedTaskValue.includes(r))
        )
      )
        return false;

      if (
        !_.isEmpty(filterDueDate.value) &&
        dueCon &&
        !allTasksForStructure.some((x) =>
          dueDateFilter({
            taskDueDate: x.dueDate ? x?.dueDate : x?.due ? x?.due : x?.nextDue,
            dueDateFilter: filterDueDate
          })
        )
      )
        return false;

      if (
        !_.isEmpty(scheduledDate.value) &&
        scheduleCon &&
        !allTasksForStructure.some((x) =>
          scheduledDateFilter({
            startDate: x.startDate || x?.NextScheduled,
            syncedDate: x.syncedDate,
            dueDateFilter: scheduledDate
          })
        )
      )
        return false;

      if (
        !_.isEmpty(createdAt?.value) &&
        createAtCon &&
        !allTasksForStructure.some((x) =>
          createAtOrSyncedDateFilter({
            dateForFilter: x?.createdDate,
            filterSelectedDate: createdAt
          })
        )
      )
        return false;

      if (
        !_.isEmpty(syncedDateFilter?.value) &&
        syncedDateCon &&
        !allTasksForStructure.some((x) =>
          createAtOrSyncedDateFilter({
            dateForFilter: x.syncedDate,
            filterSelectedDate: syncedDateFilter
          })
        )
      )
        return false;

      return true;
    })
    .sort((a, b) => {
      switch (SelectedFilter[0]) {
        case 'DUE':
          return dueDateSort(a, b);

        case 'DISTANCE':
          return distanceFilter(a, b, sitesMap, cacheSites);
        case 'A-Z':
          return a.name.localeCompare(b.name);

        case '# TASKS':
          return b?.allotDoneTasks?.length - a?.allotDoneTasks?.length;

        default:
          return a?.name?.localeCompare(b.name);
      }
    })
    ?.map((a) => {
      const dataPoints = a?.dataPointsForSearch || [];
      const cleanInput = a?.dataPointsForSearch?.length > 0 ? cleanString(searched) : '';

      let newDataPointsList = dataPoints.filter((item) => {
        let titleAndValueCombineObject = `${item.title} : ${item.value}`;
        return cleanString(String(titleAndValueCombineObject)).includes(cleanInput);
      });

      const siteDataPoints = a?.siteDataPointsForSearch || [];
      const cleanInputForSiteDataPoints =
        a?.siteDataPointsForSearch?.length > 0 ? cleanString(searched) : '';

      let newDataPointsForSiteList = siteDataPoints.filter((item) => {
        let titleAndValueCombineObject = `${item.title} : ${item.value}`;
        return cleanString(String(titleAndValueCombineObject)).includes(
          cleanInputForSiteDataPoints
        );
      });

      return {
        ...a,
        dataPoints: searched ? [...a.dataPoints, ...newDataPointsList] : a?.dataPoints,
        siteDataPoints:
          a?.dataPointsForSite?.length > 0 || a?.siteDataPointsForSearch?.length > 0
            ? searched
              ? [...a.dataPointsForSite, ...newDataPointsForSiteList]
              : a?.dataPointsForSite
            : []
      };
    });

  return {
    filteredStructure,
    sitesSiteTypeData,
    sitesSiteStatusData,
    structuresStructureTypeData,
    structuresStructureStatusData,
    sitesAreaHierarchyTreeData,
    sitesWorkOrderPriorityDataMap
  };
};

export const getFilterStructureWithPromise = ({ args }) => {
  return new Promise((resolve) => {
    const response = getFilterStructure({ ...args });
    resolve(response);
  });
};

const dueDateSort = (a, b) => {
  if (!a.soonestDueDate || !b.soonestDueDate) {
    return a.soonestDueDate ? -1 : 1;
  }

  const dateA = new Date(a.soonestDueDate);
  const dateB = new Date(b.soonestDueDate);

  if (dateA < dateB) {
    return -1;
  } else if (dateA > dateB) {
    return 1;
  } else {
    return 0;
  }
};

const distanceFilter = (a, b, sitesMap, cacheSites) => {
  return (
    (cacheSites[sitesMap[a?.siteId]]?.distance != null
      ? cacheSites[sitesMap[a?.siteId]]?.distance
      : Infinity) -
      (cacheSites[sitesMap[b?.siteId]]?.distance != null
        ? cacheSites[sitesMap[b?.siteId]]?.distance
        : Infinity) || a?.name?.localeCompare(b.name)
  );
};

export const tagsDataList = ['DISTANCE', '# TASKS', 'DUE'];

export const mapValues = ({
  sitesSiteTypeDataMap,
  sitesSiteStatusDataMap,
  sitesAssignableUsersDataMap,
  sitesCheckPriorityDataMap,
  sitesWorkOrderPriorityDataMap,
  sitesAreaHierarchyTreeDataMap,
  structuresStructureTypeDataMap,
  structuresStructureStatusDataMap,
  structures = [],
  store
}) => {
  const { picklists, tasks: reduxTasks, checks } = store;
  const { workOrderUsersGroupsMap = {}, tasksList = [] } = reduxTasks;

  const {
    siteTypeData = [],
    siteStatusData = [],
    equipmentStatusData = [],
    structureTypeData = [],
    checkPriorityData = [],
    assignableUsersData = [],
    workOrderPriorityData = [],
    areaHierarchyTreeData = [],
    picklistsMap = {}
  } = picklists;
  const { checkTypesMap } = checks;

  const { workOrderTypeTags, workOrderTypeTagMap } =
    getWorkOrderTypeTags(structures, picklistsMap) || [];

  let siteTypeTags = sitesSiteTypeDataMap
    ? siteTypeData?.filter(({ key }) => sitesSiteTypeDataMap?.[key])
    : siteTypeData;

  siteTypeTags = sortFilterTags({
    list: siteTypeTags,
    isCheckField: true,
    fieldName: 'value'
  });

  let siteStatusTags = sitesSiteStatusDataMap
    ? siteStatusData?.filter(({ key }) => sitesSiteStatusDataMap[key])
    : siteStatusData;

  siteStatusTags = sortFilterTags({
    list: siteStatusTags,
    isCheckField: true,
    fieldName: 'value'
  });

  let structureStatusTags = structuresStructureStatusDataMap
    ? equipmentStatusData?.filter(({ key }) => structuresStructureStatusDataMap[key])
    : equipmentStatusData;

  structureStatusTags = sortFilterTags({
    list: structureStatusTags,
    isCheckField: true,
    fieldName: 'value'
  });

  let structureTypeTags = structuresStructureTypeDataMap
    ? structureTypeData?.filter(({ key }) => structuresStructureTypeDataMap[key])
    : structureTypeData;

  structureTypeTags = sortFilterTags({
    list: structureTypeTags,
    isCheckField: true,
    fieldName: 'value'
  });

  const assignedUserGroups = getStructureTaskAssignedUserGroups(structures);
  const assignableUsersTags = getTaskAssignedToTags({
    assignedUserGroups,
    workOrderUsersGroupsMap
  });

  let checkPriorityTags = sitesCheckPriorityDataMap
    ? checkPriorityData.filter(({ key }) => sitesCheckPriorityDataMap[key])
    : checkPriorityData;

  checkPriorityTags = sortFilterTags({
    list: checkPriorityTags,
    isCheckField: true,
    fieldName: 'value'
  });

  let workOrderPriorityTags = sitesWorkOrderPriorityDataMap
    ? workOrderPriorityData.filter(({ key }) => sitesWorkOrderPriorityDataMap[key])
    : workOrderPriorityData;

  workOrderPriorityTags = sortFilterTags({
    list: workOrderPriorityTags,
    isCheckField: true,
    fieldName: 'value'
  });

  const treeData = sitesAreaHierarchyTreeDataMap
    ? removeUnavailableTreeData(
        JSON.parse(JSON.stringify(areaHierarchyTreeData)),
        sitesAreaHierarchyTreeDataMap
      )
    : areaHierarchyTreeData;

  const taskTypeTag = getStructuresTaskTypeTags(structures);
  const taskStatusTags = getStructuresTaskStatusTags(structures);
  const taskRecurringTemplateTags = getTaskRecurringTemplateTags(structures) || [];

  const checkRecurrancePriorityTags = getCheckRecurrancePriorityTags(structures, picklistsMap);
  const { checkTypeTags, checkTypeTagsMap } = getCheckTypeTags(structures, checkTypesMap);
  return {
    siteTypeTags,
    siteStatusTags,
    assignableUsersTags,
    checkPriorityTags,
    workOrderPriorityTags,
    treeData,
    taskTypeTag,
    taskStatusTags,
    taskRecurringTemplateTags,
    workOrderTypeTags,
    workOrderTypeTagMap,
    structureStatusTags,
    structureTypeTags,
    checkRecurrancePriorityTags,
    checkTypeTags,
    checkTypeTagsMap
  };
};

const getCheckTypeTags = (structures = [], checkTypesMap = {}) => {
  let checkTypeTags = [];

  for (let equipment of structures) {
    const equipmentTask = equipment?.allTasksForStructure || [];
    for (let task of equipmentTask) {
      if (task?.type === 'Check' && checkTypesMap[task?.inspectionTypeId]) {
        if (
          checkTypesMap[task?.inspectionTypeId] &&
          !checkTypeTags.includes(checkTypesMap[task?.inspectionTypeId])
        )
          checkTypeTags.push(checkTypesMap[task?.inspectionTypeId]);
      }
    }
  }

  const checkTypeTagsMap = checkTypeTags?.reduce((acc, curr) => {
    acc[curr.key] = { value: curr.value };
    return acc;
  }, {});

  checkTypeTags = sortFilterTags({
    list: checkTypeTags,
    isCheckField: true,
    fieldName: 'value'
  });

  return { checkTypeTags, checkTypeTagsMap };
};

const getCheckRecurrancePriorityTags = (structures = [], picklistsMap) => {
  let checkRecurrenceTags = [];

  for (let equipment of structures) {
    const equipmentTask = equipment?.allTasksForStructure || [];
    for (let task of equipmentTask) {
      if (
        task?.type === 'Check' &&
        task?.recurrencePriorityId &&
        picklistsMap[task?.recurrencePriorityId]
      ) {
        let recurreanceValue = picklistsMap[task?.recurrencePriorityId] || {};
        let recurreanceValueCopy = { ...recurreanceValue };
        recurreanceValueCopy.value = recurreanceValue?.value?.split(' ')[0] || '';
        if (!checkRecurrenceTags.some((tag) => tag.value === recurreanceValueCopy.value)) {
          checkRecurrenceTags.push(recurreanceValueCopy);
        }
      }
    }
  }

  checkRecurrenceTags = sortFilterTags({
    list: checkRecurrenceTags,
    isCheckField: true,
    fieldName: 'value'
  });

  return checkRecurrenceTags;
};

const getWorkOrderTypeTags = (structures = [], picklistsMap = {}) => {
  let workOrderTypeTags = [];

  structures.map((item) =>
    item?.allTasksForStructure?.map((task) => {
      if (picklistsMap[task?.typeId] && !workOrderTypeTags.includes(picklistsMap[task?.typeId]))
        workOrderTypeTags.push(picklistsMap[task?.typeId]);
    })
  );

  const workOrderTypeTagMap = workOrderTypeTags?.reduce((acc, curr) => {
    acc[curr.key] = { value: curr.value };
    return acc;
  }, {});

  return { workOrderTypeTags, workOrderTypeTagMap };
};

const getStructureTaskAssignedUserGroups = (structures) => {
  let assignedUserGroups = [];
  for (let structure of structures) {
    if (_.size(structure.allTasksForStructure) > 0) {
      assignedUserGroups.push(
        Array.from(
          new Set(structure.allTasksForStructure.map((t) => t?.responsibleUserGroups).flat(2))
        ) || []
      );
    }
  }
  return Array.from(new Set(_.flattenDeep(assignedUserGroups))) || [];
};

const getTaskAssignedToTags = ({ assignedUserGroups, workOrderUsersGroupsMap }) => {
  let data =
    (assignedUserGroups && // responsible users groups
      _.size(assignedUserGroups) > 0 &&
      assignedUserGroups
        .map((key) => workOrderUsersGroupsMap[key] && workOrderUsersGroupsMap[key]?.name)
        .filter((y) => y)) ||
    [];

  let assignableUsersTags = ['Me, directly', 'A group I’m in', ...data];

  return assignableUsersTags;
};

const getStructuresTaskTypeTags = (structures) => {
  let taskTypeTag = [];
  for (let structure of structures) {
    if (
      _.size(structure.allTasksForStructure) > 0 &&
      structure.allTasksForStructure.some((x) => x.type === 'Work Order')
    ) {
      taskTypeTag.push('Work orders');
      break;
    }
  }
  for (let structure of structures) {
    if (
      _.size(structure.allTasksForStructure) > 0 &&
      structure.allTasksForStructure.some((x) => x.type === 'Work Request' || x.newWorkRequest)
    ) {
      taskTypeTag.push('Work requests');
      break;
    }
  }
  for (let structure of structures) {
    if (
      _.size(structure.allTasksForStructure) > 0 &&
      structure.allTasksForStructure.some((x) => x.type === 'Inspection')
    ) {
      taskTypeTag.push('Inspections');
      break;
    }
  }
  for (let structure of structures) {
    if (
      _.size(structure.allTasksForStructure) > 0 &&
      structure.allTasksForStructure.some((x) => x.type === 'Check')
    ) {
      taskTypeTag.push('Check');
      break;
    }
  }

  for (let structure of structures) {
    if (
      _.size(structure?.allEditEquipmentAssets) > 0 &&
      structure.allEditEquipmentAssets.some((x) => x.type === 'New or edited asset')
    ) {
      taskTypeTag.push('New or edited asset');
      break;
    }
  }

  return taskTypeTag?.length > 0 ? [...taskTypeTag, 'All'] : taskTypeTag;
};

const getStructuresTaskStatusTags = (structures) => {
  let taskStatusTags = [];
  for (let structure of structures) {
    if (
      _.size(structure.allTasksForStructure) > 0 &&
      structure.allTasksForStructure.some((x) => x.status === 'To do')
    ) {
      taskStatusTags.push('To do');
      break;
    }
  }
  for (let structure of structures) {
    if (
      _.size(structure.allTasksForStructure) > 0 &&
      structure.allTasksForStructure.some((x) => x.status === 'In progress')
    ) {
      taskStatusTags.push('In progress');
      break;
    }
  }
  for (let structure of structures) {
    if (
      _.size(structure.allTasksForStructure) > 0 &&
      structure.allTasksForStructure.some((x) => x.status === 'Pending sync')
    ) {
      taskStatusTags.push('Pending sync');
      break;
    }
  }
  for (let structure of structures) {
    if (
      _.size(structure.allTasksForStructure) > 0 &&
      structure.allTasksForStructure.some((x) => x.status === 'Synced')
    ) {
      taskStatusTags.push('Synced');
      break;
    }
  }
  return taskStatusTags;
};

const getTaskRecurringTemplateTags = (structures = []) => {
  let tags = Array.from(
    new Set(
      structures
        .map(
          (item) =>
            item.allTasksForStructure &&
            item.allTasksForStructure.map((task) => {
              if (task.rtNumber) return `${task.rtNumber} - ${task.rtName}`;
            })
        )
        .flat()
        .filter((name) => name)
    )
  );

  tags = sortFilterTags({
    list: tags,
    isCheckField: false,
    fieldName: ''
  });
  return tags;
};

export const countFilters = (filters, inputValue, store) => {
  let count = 0;
  const { picklists } = store;

  const {
    proximity,
    area,
    siteType,
    siteStatus,
    taskType,
    taskStatus,
    assign,
    priority,
    workOrderPriority,
    structureType,
    structureStatus,

    workOrderType = { value: [] },
    recurringTemplate = { value: [] },
    checkRecurrancePriority = { value: [] },
    inspectionType = { value: [] },
    checkType = { value: [] }
  } = filters;

  if (inputValue?.trim()) ++count;
  if (recurringTemplate?.value?.length > 0) ++count;
  if (proximity.value > 0) ++count;
  if (workOrderType?.value?.length > 0) ++count;
  if (assign?.value?.length > 0) ++count;
  if (siteType?.value?.length > 0) ++count;
  if (taskType?.value?.length > 0) ++count;
  if (checkType?.value?.length > 0) ++count;
  if (taskStatus?.value?.length > 0) ++count;
  if (siteStatus?.value?.length > 0) ++count;
  if (structureType?.value?.length > 0) ++count;
  if (structureStatus?.value?.length > 0) ++count;
  if (checkRecurrancePriority?.value.length > 0) ++count;
  if (inspectionType?.value?.length > 0) ++count;
  if (priority?.value?.length > 0 || workOrderPriority?.value?.length > 0) ++count;
  if (
    area?.value &&
    area?.value.filter((el) => picklists.areaHierarchyTreeDataMap?.[el])?.length > 0
  )
    ++count;

  ['dueDate', 'scheduledDate', 'createdAt', 'syncedDate'].forEach((key) => {
    const { value, selected } = filters[key] || { value: '', selected: '' };
    if (
      selected &&
      !_.isEmpty(value) &&
      (selected === 'on'
        ? value !== ''
        : selected === 'range'
        ? value?.[0] !== '' && value?.[0] !== ''
        : value?.length > 0)
    )
      ++count;
  });
  return count;
};

export const getStructureDefaultFilters = {
  structureType: { value: [] },
  structureStatus: { value: [] },
  proximity: { value: 0 },
  area: { value: [] },
  siteType: { value: [] },
  siteStatus: { value: [] },
  lastSiteCheckIn: { value: [], selected: 'past' },
  taskType: { value: [] },
  taskStatus: { value: [] },
  assign: { value: [] },
  priority: { value: [] },
  workOrderPriority: { value: [] },
  download: { value: '', selected: 'past' },
  createdAt: { value: '', selected: 'past' },
  syncedDate: { value: '', selected: 'past' },
  dueDate: { value: [], selected: 'next' },
  scheduledDate: { value: [], selected: 'next' },
  recurringTemplate: { value: [] },
  workOrderType: { value: [] },
  checkRecurrancePriority: { value: [] },
  inspectionType: { value: [] },
  checkType: { value: [] }
};
